import {
  CancelSubscriptionProps,
  ChangeOrderDateModalProps,
  ChangeOrderDateProps,
  CurrentSubscriptionCardProps,
  PauseSubscriptionModalProps,
  PauseSubscriptionProps,
  ShipmentInfoProps,
  SubscriptionInfoProps,
  AddNewPaymentMethodCopy,
  CancelOfferModalProps,
  CancelConfirmationModalProps,
  ImageProps,
} from "@bluebottlecoffee/design-system/components";
import { loadStripe } from "@stripe/stripe-js";
import {
  CancellationReason,
  PaymentSource,
  SubscriptionState,
  PauseSubscriptionOptions,
  UpdateSubscriptionOptions,
  UseSubscriptionHook,
} from "@bluebottlecoffee/design-system/components/lib/types";
import { noop } from "lodash";
import {
  useSubscription as chordUseSubscription,
  PauseSubscription,
  ResumeSubscription,
  Subscription,
  SubscriptionUpdateProps,
} from "@chordcommerce/react-autonomy";
import { addWeeks, startOfTomorrow } from "date-fns";
import { Dispatch, SetStateAction } from "react";
import { noopPromiseVoid } from "@bluebottlecoffee/design-system/components/utils/noop";
import { EditSubscriptionState } from "@bluebottlecoffee/design-system/components/Account/Subscriptions/useEditSubscription";
import { trackEvent } from "../segment/track-event";
import useVariantPrice from "../chord/hooks/components/use-variant-price";
import useVariantAvailability from "../chord/hooks/components/use-variant-availability";
import { SubsUpdatedType } from "../../pages/[region]/[lang]/account/current-subscriptions";
import {
  toEventSubscriptionLineItem,
  toSubscriptionEventProps,
  trackSubscriptionCancellationDeflected,
  trackSubscriptionCancellationStarted,
  trackSubscriptionOfferAccepted,
} from "../segment/track-subscription";
import {
  StripeBillAddress,
  transformShippingAddress,
} from "../component-utils/account-addresses";
import { SanityQueryResultType } from "../sanity/current-subscriptions-queries";
import { createShipmentDateObjects, defaultImage } from "../utils";
import { toImageProps } from "./image";

function useSubscription(subscriptionId: number): UseSubscriptionHook {
  return chordUseSubscription({
    subscriptionId,
  }) as unknown as UseSubscriptionHook;
}

export const generateUniqueKey = () =>
  `id${Math.random().toString(16).slice(2)}`;

function useSubscriptionAndUpdateSubState(
  subscriptionId: number,
  setSubsUpdated: Dispatch<SetStateAction<SubsUpdatedType>>,
): UseSubscriptionHook {
  const subscriptionChordHandlers = chordUseSubscription({ subscriptionId });

  const {
    pauseSubscription,
    resumeSubscription,
    skipSubscription,
    updateSubscription,
    cancelSubscription,
    subscription,
  } = subscriptionChordHandlers;

  const eventProps = toSubscriptionEventProps(
    subscription,
    toEventSubscriptionLineItem,
  );

  const handleUpdateSub = () => {
    setSubsUpdated((prev) => {
      const newState: SubsUpdatedType = prev
        .filter((subUpdated) => subUpdated.id !== subscriptionId)
        .map((subUpdated) => ({
          ...subUpdated,
          latest: false,
        }));

      return [...newState, { id: subscriptionId, updated: true, latest: true }];
    });
  };

  return {
    ...subscriptionChordHandlers,
    pauseSubscription: async (options?: PauseSubscriptionOptions) => {
      try {
        return await pauseSubscription(options as PauseSubscription);
      } finally {
        handleUpdateSub();
        trackEvent("subscription_pause", eventProps);
      }
    },
    resumeSubscription: async (options?: PauseSubscriptionOptions) => {
      try {
        return await resumeSubscription(options as ResumeSubscription);
      } finally {
        handleUpdateSub();
        trackEvent("subscription_resume", eventProps);
      }
    },
    skipSubscription: async () => {
      try {
        return await skipSubscription();
      } finally {
        handleUpdateSub();
        trackEvent("subscription_skip", eventProps);
      }
    },
    updateSubscription: async (options: UpdateSubscriptionOptions) => {
      try {
        return await updateSubscription(
          options as unknown as SubscriptionUpdateProps,
        );
      } finally {
        handleUpdateSub();
        const lineItem = subscription.lineItems[0];
        const newLineItem = options.lineItemsAttributes?.[0];

        trackEvent("subscription_update", {
          ...eventProps,
          update: {
            sku:
              newLineItem?.sku !== lineItem.sku
                ? newLineItem?.sku || null
                : null,
            quantity:
              newLineItem?.quantity !== lineItem.quantity
                ? newLineItem?.quantity || null
                : null,
            intervalLength:
              options.intervalLength !== lineItem.intervalLength
                ? options.intervalLength || null
                : null,
            intervalUnits:
              options.intervalUnits !== lineItem.intervalUnits
                ? options.intervalUnits || null
                : null,
          },
        });
      }
    },
    cancelSubscription: async () => {
      try {
        return await cancelSubscription();
      } finally {
        handleUpdateSub();
        trackEvent("subscription_cancel", eventProps);
      }
    },
  };
}

export type CurrentSubscriptionCardTransformerProps = {
  sub: Subscription;
  sanityQueryResult: SanityQueryResultType;
  productionDays: number[];
  onDateClick: ChangeOrderDateProps["onDateClick"];
};

const getNextScheduledRoast = (sub: Subscription) =>
  sub.actionableDate
    ? new Date(sub.actionableDate)
    : new Date(startOfTomorrow());

function toChangeOrderDateProps({
  sub,
  sanityQueryResult: {
    copy,
    coffeeFulfillmentHolidays,
    coffeeFulfillmentCutOff,
  },
  productionDays,
  onDateClick,
}: CurrentSubscriptionCardTransformerProps): ChangeOrderDateProps {
  const flattenedCoffeeFulfillmentHolidays = coffeeFulfillmentHolidays.flatMap(
    (year) => year.dates.flatMap((date) => new Date(`${date}T00:00`)),
  );

  return {
    calendarCopy: copy.subscriptionCalendarCopy,
    coffeeFulfillmentCutOff: coffeeFulfillmentCutOff.map(
      ({ time }) => new Date(Date.parse(time)),
    )[0], // Take the first one?
    coffeeFulfillmentHolidays: flattenedCoffeeFulfillmentHolidays,
    nextScheduledRoast: getNextScheduledRoast(sub),
    productionDays,
    onDateClick,
  };
}

type ChangeOrderDateModalTransformerProps = {
  sub: Subscription;
  sanityQueryResult: SanityQueryResultType;
  productionDays: number[];
  onSave: ChangeOrderDateModalProps["onSave"];
  onDateClick: ChangeOrderDateProps["onDateClick"];
};

function toChangeOrderDateModalProps({
  sub,
  sanityQueryResult,
  productionDays,
  onSave,
  onDateClick,
}: ChangeOrderDateModalTransformerProps): ChangeOrderDateModalProps {
  return {
    changeOrderDateProps: toChangeOrderDateProps({
      sub,
      sanityQueryResult,
      productionDays,
      onDateClick,
    }),
    nextScheduledRoast: getNextScheduledRoast(sub),
    onSave,
  };
}

export function toPaymentMethodProps(
  { source }: any,
  copy: SanityQueryResultType["copy"],
): PaymentSource {
  const { id, month, year, ccType, lastDigits, name, memo } = source;
  if (ccType) {
    return {
      id,
      month: Number(month),
      year: Number(year),
      ccType,
      lastDigits: Number(lastDigits),
      name,
      paymentMethod: "creditCard",
    };
  }

  if (memo) {
    return {
      id,
      creditLabel: copy.shipmentInfoCopy.creditLabel,
      paymentMethod: "storeCredit",
    };
  }

  return null;
}

type SubsAddNewPaymentMethodTransformerProps = {
  sanityQueryResult: SanityQueryResultType;
  lang: string;
};

function toSubsAddNewPaymentMethodProps({
  lang,
  sanityQueryResult: { currentSubscriptionsPage },
}: SubsAddNewPaymentMethodTransformerProps): AddNewPaymentMethodCopy {
  const {
    heading,
    paymentMethodSectionHeading,
    returnToPrevious,
    transactionAssurance,
  } = currentSubscriptionsPage.updatePaymentCta;
  return {
    heading: heading[lang],
    paymentMethodSectionHeading: paymentMethodSectionHeading[lang],
    returnToPrevious: returnToPrevious[lang],
    transactionAssurance: transactionAssurance[lang],
  };
}

type ModalDetailsCopy = {
  ctaText: string;
  errorMessage: string;
  successMessage: string;
};

type ModalDetailTransformerProps = {
  lang: string;
  sanityQueryResult: SanityQueryResultType;
};

function toModalDetailsCopy({
  lang,
  sanityQueryResult: { currentSubscriptionsPage },
}: ModalDetailTransformerProps): ModalDetailsCopy {
  const { ctaText, errorMessage, successMessage } =
    currentSubscriptionsPage.updatePaymentCta;
  return {
    ctaText: ctaText[lang],
    errorMessage: errorMessage[lang],
    successMessage: successMessage[lang],
  };
}

type BillAddressTransformerProps = {
  sub: Subscription;
};

function toBillAddress({
  sub: { billAddress },
}: BillAddressTransformerProps): StripeBillAddress["address"] {
  const { address1, address2, city, countryIso, stateText, zipcode } =
    billAddress;
  return {
    line1: address1,
    line2: address2,
    city,
    country: countryIso,
    state: stateText,
    postal_code: zipcode,
  };
}

type ShipmentInfoTransformerProps = {
  sub: Subscription;
  sanityQueryResult: SanityQueryResultType;
  lang: string;
  productionDays: number[];
  onChangeOrderModalSave: ChangeOrderDateModalProps["onSave"];
  onChangeOrderDateClick: ChangeOrderDateProps["onDateClick"];
} & Pick<
  ShipmentInfoProps,
  "onAddressInput" | "addressPredictions" | "onSelectAddressPrediction"
>;

export function toShipmentInfoProps({
  lang,
  productionDays,
  sub,
  sanityQueryResult,
  onAddressInput,
  addressPredictions,
  onSelectAddressPrediction,
  onChangeOrderDateClick: onDateClick,
  onChangeOrderModalSave: onSave,
}: ShipmentInfoTransformerProps): ShipmentInfoProps {
  const { id, payment } = sub;
  const { copy } = sanityQueryResult;
  const { addressFormCopy, shipmentInfoCopy } = copy;

  const createdAtDate = new Date(sub.createdAt);
  const { currentShipmentDate, nextShipmentDate } = createShipmentDateObjects({
    intervalUnits: sub.intervalUnits,
    intervalLength: sub.intervalLength,
    actionableDate: sub.actionableDate,
  });

  return {
    copy: {
      addressFormCopy,
      ...shipmentInfoCopy,
    },
    createdAtDate,
    currentShipmentDate,
    nextShipmentDate,
    shippingAddress: transformShippingAddress(sub.shipAddress),
    paymentMethod: payment ? toPaymentMethodProps(payment, copy) : null,
    propsChangeOrderDateModal: toChangeOrderDateModalProps({
      sub,
      sanityQueryResult,
      productionDays,
      onDateClick,
      onSave,
    }),
    propsConfirmSkipNextShipmentModal: {
      confirmSkipNextShipmentProps: {
        copy: { ...copy.confirmSkipNextShipmentCopy },
      },
    },
    updateAddressCta: {
      text: shipmentInfoCopy.updateAddressCta,
      url: "/",
    },
    updatePaymentCta: {
      addNewPaymentMethodProps: {
        copy: toSubsAddNewPaymentMethodProps({
          lang,
          sanityQueryResult,
        }),
        stripePromise: loadStripe(process.env.NEXT_PUBLIC_STRIPE_PK),
        modalDetails: {
          billAddress: {
            name: sub.billAddress.name,
            address: toBillAddress({ sub }),
          },
          copy: toModalDetailsCopy({ lang, sanityQueryResult }),
          subscriptionId: id,
          onSuccess: () => {},
          useSubscription,
        },
      },
      triggerText: shipmentInfoCopy.updatePaymentCta,
    },
    subscriptionId: id,
    useSubscription,
    onAddressInput,
    addressPredictions,
    onSelectAddressPrediction,
  };
}

export type SubscriptionUpdateType = "product" | "bagSize";

type SubscriptionInfoTransformerProps = {
  sanityQueryResult: SanityQueryResultType;
  sub: Subscription;
};

export function toSubscriptionInfoProps({
  sanityQueryResult: {
    copy: { subscriptionInfoCopy },
    subscribableProducts,
  },
  sub,
}: SubscriptionInfoTransformerProps): SubscriptionInfoProps {
  const {
    bagSizeHeader,
    changeSubscriptionConfirmButtonText,
    changeSubscriptionConfirmBodyText,
    changeSubscriptionConfirmHeader,
    giftInstallmentsTitle,
    changeSubscriptionSelectionHeader,
    coffeeTypeHeader,
    coffeeTypePerShipment,
    deliveryScheduleHeader,
    editSubscriptionCtaLabel,
    editSubscriptionTitle,
    updatedPopupLabel,
    updatedPopupText,
  } = subscriptionInfoCopy;
  const subLineItem =
    // @ts-ignore Chord SubscriptionLineItem type is missing the oneTime: boolean
    // but the API response includes this. Issue acknowledged by Chord 7 - 8 - 24
    sub.lineItems.find((i) => !i.oneTime) ?? sub.lineItems[0];
  const subscriptionOptions = subscribableProducts?.find((card) =>
    card.product.variants?.find((v) => v.sku === subLineItem.sku),
  );

  const subscriptionImage: ImageProps = subscriptionOptions?.image || {
    altText: "",
    src: "/v1612883439/blue_bottle_brand_assets/Bottle_Logo-Small.001_031820.png",
  };

  return {
    copy: {
      coffeeType: {
        header: coffeeTypeHeader,
        perShipment: coffeeTypePerShipment,
      },
      editSubscriptionCtaLabel,
      editSubscriptionTitle,
      updatedPopupLabel,
      updatedPopupText,
      giftInstallmentsTitle,
    },
    changeSubscriptionProductModalProps: {
      confirmText: changeSubscriptionConfirmButtonText,
      confirmHeader: changeSubscriptionConfirmHeader,
      confirmBodyText: changeSubscriptionConfirmBodyText,
      currentSubscriptionId: sub.id,
      headerText: changeSubscriptionSelectionHeader,
      nextSubscriptionShipmentDate: sub.actionableDate,
      subscribableProducts: subscribableProducts.map((card) => ({
        ...card,
        useVariantAvailability,
        addToCart: noopPromiseVoid,
        createStockRequest: noopPromiseVoid as any,
        subscribeProduct: noopPromiseVoid,
        shippingCopy: card.shippingCopy,
      })),
      onEdit: noop,
    },
    itemDetails: {
      productName: subLineItem.name,
      image: {
        desktop: subscriptionImage,
      },
      price: {
        amount: parseFloat(subLineItem.price) * 100 * subLineItem.quantity,
        currency: "USD", // TODO
      },
      discountPriceFn: () =>
        useVariantPrice({
          sku: subLineItem.sku,
          quantity: subLineItem.quantity,
          intervalLength: subLineItem.intervalLength,
          intervalUnits: subLineItem.intervalUnits,
        }),
      description: subLineItem.description,
      initialQuantity: subLineItem.quantity,
      frequency: sub.interval,
      sku: subLineItem.sku,
    },
    bagSizeDetails: {
      copy: {
        header: bagSizeHeader,
      },
      bagSizeOptions: subscriptionOptions?.product?.subscriptionOptions ?? [],
    },
    deliveryScheduleDetails: {
      copy: {
        header: deliveryScheduleHeader,
      },
      deliveryScheduleOptions:
        subscriptionOptions?.product?.subscription?.intervals ?? [],
      currentInterval: {
        length: sub.intervalLength,
        unit: sub.intervalUnits,
      },
    },
  };
}

function toPauseSubscriptionProps({
  sub,
  sanityQueryResult,
  productionDays,
  onDateClick,
  onResumeDateClick,
}): PauseSubscriptionProps {
  const { copy } = sanityQueryResult;

  const { currentShipmentDate, nextShipmentDate } = createShipmentDateObjects({
    intervalUnits: sub.intervalUnits,
    intervalLength: sub.intervalLength,
    actionableDate: sub.actionableDate,
  });

  return {
    copy: copy.pauseSubscriptionCopy,
    currentShipmentDate,
    nextShipmentDate,
    onResumeDateClick,
    propsChangeOrderDate: toChangeOrderDateProps({
      sub,
      sanityQueryResult,
      productionDays,
      onDateClick,
    }),
  };
}

export function toPauseSubscriptionModalProps({
  sub,
  sanityQueryResult,
  productionDays,
  onDateClick,
  onResumeDateClick,
}): PauseSubscriptionModalProps {
  return {
    pauseSubscriptionProps: toPauseSubscriptionProps({
      sub,
      sanityQueryResult,
      productionDays,
      onDateClick,
      onResumeDateClick,
    }),
    onDeflectionSubmit: trackSubscriptionOfferAccepted(sub),
    onDeflectionUserDismiss: trackSubscriptionCancellationDeflected(sub),
  };
}

type CancelOfferModalTransformerProps = {
  lang: string;
  sanityQueryResult: SanityQueryResultType;
};

function toCancelOfferModalProps({
  lang,
  sanityQueryResult: {
    copy: {
      cancelSubscriptionCopy,
      pauseSubscriptionCopy,
      confirmSkipNextShipmentCopy,
    },
    cancelOfferImage,
  },
}: CancelOfferModalTransformerProps): Omit<CancelOfferModalProps, "ctas"> & {
  ctas: CancelSubscriptionProps["offers"]["ctas"];
} {
  return {
    copy: cancelSubscriptionCopy,
    ctas: [
      {
        onEvent: noop,
        flow: "skip",
        text: confirmSkipNextShipmentCopy.modalTriggerText,
      },
      {
        onEvent: noop,
        flow: "pause",
        text: pauseSubscriptionCopy.pauseModalTrigger,
      },
      {
        onEvent: noop,
        flow: "cancel",
        text: cancelSubscriptionCopy.cancelTriggerText,
      },
    ],
    image: cancelOfferImage
      ? toImageProps(cancelOfferImage, lang)
      : defaultImage,
  };
}

type CancelConfirmationModalTransformerProps = {
  lang: string;
  sanityQueryResult: SanityQueryResultType;
  onClick: CancelConfirmationModalProps["onClick"];
};

function toCancelConfirmationModalProps({
  lang,
  sanityQueryResult: {
    copy: { cancelSubscriptionCopy },
    cancelConfirmationImage,
  },
  onClick,
}: CancelConfirmationModalTransformerProps): CancelConfirmationModalProps {
  return {
    copy: cancelSubscriptionCopy,
    image: cancelConfirmationImage
      ? toImageProps(cancelConfirmationImage, lang)
      : defaultImage,
    onClick,
  };
}

type CancelSubscriptionTransformerProps = {
  lang: string;
  sanityQueryResult: SanityQueryResultType;
  sub: Subscription;
  onCancelConfirmClick: CancelConfirmationModalProps["onClick"];
};

export function toCancelSubscriptionProps({
  lang,
  sanityQueryResult,
  sub,
  onCancelConfirmClick,
}: CancelSubscriptionTransformerProps): CancelSubscriptionProps {
  const { copy, cancellationReasons } = sanityQueryResult;
  const { cancelSubscriptionCopy } = copy;

  return {
    confirmationProps: toCancelConfirmationModalProps({
      lang,
      sanityQueryResult,
      onClick: onCancelConfirmClick,
    }),
    cta: {
      onEvent: noop,
      text: cancelSubscriptionCopy.next,
    },
    description: cancelSubscriptionCopy.body,
    cancelLink: {
      text: cancelSubscriptionCopy.cancelTriggerText,
    },
    offers: toCancelOfferModalProps({ lang, sanityQueryResult }),
    reasons: cancellationReasons.map<CancellationReason>(({ id, reason }) => ({
      id,
      text: reason,
    })),
    title: cancelSubscriptionCopy.header,
    onOpen: trackSubscriptionCancellationStarted(sub),
    onUserDismiss: trackSubscriptionCancellationDeflected(sub),
  };
}

type prepaidSubscriptionExtended = Subscription["prepaidSubscription"] & {
  startDate: Date;
  fulfilledInstallmentCount: number;
};

const normalizePrepaidData = (sub: Subscription) => {
  if (!sub.prepaidSubscription) return null;

  const {
    installmentCount,
    intervalLength,
    startDate,
    fulfilledInstallmentCount,
  } = sub.prepaidSubscription as prepaidSubscriptionExtended; // For some reason startDate and fulfilledInstallmentCount doesn't exist in prepaidSubscription type :(
  const installmentRemaining = installmentCount - fulfilledInstallmentCount;

  return {
    installmentRemaining,
    endDate: addWeeks(new Date(startDate), installmentCount * intervalLength),
  };
};

export type HandleEditSubscriptionType = {
  editSubscriptionData: EditSubscriptionState;
  subscriptionController: UseSubscriptionHook;
};

type SubscriptionCardTransformerProps = {
  sub: Subscription;
  sanityQueryResult: SanityQueryResultType;
  lang: string;
  productionDays: number[];
  onCancelConfirmClick: CancelConfirmationModalProps["onClick"];
  onChangeOrderModalSave: ChangeOrderDateModalProps["onSave"];
  onChangeOrderDateClick: ChangeOrderDateProps["onDateClick"];
  onResumeDateClick: PauseSubscriptionProps["onResumeDateClick"];
  handleEditSubscription: (
    data: HandleEditSubscriptionType,
    sub: Subscription,
  ) => Promise<unknown>;
  setSubsUpdated: Dispatch<SetStateAction<SubsUpdatedType>>;
  subsUpdated: SubsUpdatedType;
} & Pick<
  ShipmentInfoProps,
  "onAddressInput" | "addressPredictions" | "onSelectAddressPrediction"
> &
  Pick<CurrentSubscriptionCardProps, "openModalFlow">;

export type CurrentSubscriptionCardTransformerReturnProps = Omit<
  CurrentSubscriptionCardProps,
  "index" | "cardOpen" | "setCardOpen"
>;

export function toSubscriptionCardProps({
  addressPredictions,
  lang,
  onAddressInput,
  onSelectAddressPrediction,
  productionDays,
  sanityQueryResult,
  sub,
  onCancelConfirmClick,
  onChangeOrderDateClick,
  onChangeOrderModalSave,
  onResumeDateClick,
  openModalFlow,
  handleEditSubscription,
  setSubsUpdated,
  subsUpdated,
}: SubscriptionCardTransformerProps): CurrentSubscriptionCardTransformerReturnProps {
  const { prepaidSubscription } = sub;
  let state = sub.state as SubscriptionState;
  const statesArray: SubscriptionState[] = [
    "active",
    "paused",
    "canceled",
    "gift",
    "prepaid",
  ];

  if (prepaidSubscription?.gift && state !== "canceled") {
    state = "gift";
  }

  if (prepaidSubscription?.gift === false && state !== "canceled") {
    state = "prepaid";
  }

  if (!statesArray.some((stateItem) => stateItem === state)) {
    state = "active";
  }

  const handleSetClosePopupUpdated = () => {
    setSubsUpdated((prev) =>
      prev.map((subUpdated) => {
        if (subUpdated.id === sub.id) {
          return {
            ...subUpdated,
            updated: false,
            latest: false,
          };
        }

        return { ...subUpdated, latest: false };
      }),
    );
  };

  return {
    handleEditSubscription: (updateObj) =>
      handleEditSubscription(updateObj, sub),
    prepaidData: normalizePrepaidData(sub),
    useSubscription: () =>
      useSubscriptionAndUpdateSubState(sub.id, setSubsUpdated),
    subscriptionUpdated: subsUpdated.find(
      (subUpdated) => subUpdated.id === sub.id,
    )?.updated,
    setClosePopupUpdated: handleSetClosePopupUpdated,
    id: sub.id,
    propsShipmentInfo: toShipmentInfoProps({
      lang,
      sub,
      sanityQueryResult,
      productionDays,
      onAddressInput,
      addressPredictions,
      onSelectAddressPrediction,
      onChangeOrderDateClick,
      onChangeOrderModalSave,
    }),
    propsSubscriptionInfo: toSubscriptionInfoProps({
      sub,
      sanityQueryResult,
    }),
    propsPauseSubscriptionModal: toPauseSubscriptionModalProps({
      sub,
      sanityQueryResult,
      productionDays,
      onDateClick: onChangeOrderDateClick,
      onResumeDateClick,
    }),
    propsCancelSubscription: toCancelSubscriptionProps({
      lang,
      sanityQueryResult,
      sub,
      onCancelConfirmClick,
    }),
    state,
    openModalFlow,
  };
}
